import { useLazyQuery } from "@apollo/client"
import { GET_INFOGO_PREVIEW } from "../../../../graphql/infogo-gql"
import { ForwardedRef, forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react"
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap"
import SpinnerOverlay from "../../../common/spinner/spinner"
import { ICategory, IOrg } from "../../../../types/org"
import "./preview.scss"
import { IInfoGoAddress, IInfoGoEmail, IInfoGoEmployee, IInfoGoNetAddress, IInfoGoOrgBase, IInfoGoPhone } from "../../../../types/infogo"
import { CancelIcon } from "../../../common/icons"
import Spacer from "../../../common/spacer"
import Button from "../../../common/button"

export type PreviewHandler = {
    open: (inter: boolean) => void
}

type PreviewProps = {
    org: IOrg
}

const Preview = forwardRef<PreviewHandler, PreviewProps>((props: PreviewProps, ref:ForwardedRef<PreviewHandler>) => {

    const {org} = props

    const [language, setLanguage] = useState<string>('EN')
    const [orgId, setOrgId] = useState<string | null>(null)
    const [inter, setInter] = useState<boolean>(false)

    const [ orgCategories, setOrgCategories ] = useState<ICategory[]>([])
    const [ associatedOrgsMap, setAssociatedOrgsMap ] = useState<Map<string, IInfoGoOrgBase[]>>(new Map<string, IInfoGoOrgBase[]>())

    const [ phoneMap, setPhoneMap ] = useState<Map<string, IInfoGoPhone[]>>(new Map<string, IInfoGoPhone[]>())
    const [ primaryAddress, setPrimaryAddress ] = useState<IInfoGoAddress>()
    const [ mailingAddress, setMailingAddress ] = useState<IInfoGoAddress>()
    const [ otherAddresses, setOtherAddresses ] = useState<IInfoGoAddress[]>([])

    const [getInfoGoPreview, { loading, data }] = useLazyQuery(GET_INFOGO_PREVIEW, {
        fetchPolicy: 'network-only'
    })

    const [isOpen, setIsOpen] = useState<boolean>(false)

    useEffect(() => {
        setOrgId(org ? org.id.toString(): null)
    }, [org])

    const toggle = useCallback(() => {
        setIsOpen(!isOpen)
        setLanguage('EN')
    }, [isOpen])

    useImperativeHandle(ref, () => ({
        open(inter: boolean) {
            setInter(inter)
            fetchOrg(org.id.toString(), 'EN', inter)
            setIsOpen(true)
        }
    }),[org])

    useEffect(() => {
        let type2PhoneMap = new Map<string, IInfoGoPhone[]>()
        if (data?.infoGoPreview?.phoneList) {
            data.infoGoPreview.phoneList.forEach((phone: IInfoGoPhone) => {
                if (!type2PhoneMap.get(phone.phoneType)) {
                    type2PhoneMap.set(phone.phoneType, [])
                }
                type2PhoneMap.get(phone.phoneType)?.push(phone)
            })
        }
        setPhoneMap(type2PhoneMap)

        if (data?.infoGoPreview?.addresses) {
            setPrimaryAddress(data.infoGoPreview.addresses.find((e: IInfoGoAddress) => e.primary))
            setMailingAddress(data.infoGoPreview.addresses.find((e: IInfoGoAddress) => e.mailing))
            setOtherAddresses(data.infoGoPreview.addresses.filter((e: IInfoGoAddress) => !e.primary && !e.mailing))
        }

        if (data?.infoGoPreview?.indirectOrgs) {
            let categories: ICategory[] = []
            let category2OrgMap = new Map<string, IInfoGoOrgBase[]>()

            data.infoGoPreview.indirectOrgs.forEach((org: IInfoGoOrgBase) => {
                if (!categories.some((cat: ICategory) => cat.id.toString() === org.categoryId)) {
                    categories.push({id: Number(org.categoryId), categoryName: org.categoryName, displayOrder: 0})
                }

                if (!category2OrgMap.get(org.categoryId)) {
                    category2OrgMap.set(org.categoryId, [])
                }
                category2OrgMap.get(org.categoryId)?.push(org)
            })

            setOrgCategories(categories)
            setAssociatedOrgsMap(category2OrgMap)
        }
    }, [data])

    const switchLang = (event: any) => {
        event.preventDefault()
        event.stopPropagation()
        let lang = language === 'EN' ? 'FR' : 'EN'
        setLanguage(lang)
        fetchOrg(orgId, lang, inter)
    }

    const selectOrg = (event: any, orgId: string) => {
        event.preventDefault()
        event.stopPropagation()
        setOrgId(orgId)
        fetchOrg(orgId, language, inter)
    }

    const fetchOrg = (orgId: string | null, lang: string, inter: boolean) => {
        if (orgId) {
            getInfoGoPreview({
                variables: {
                    orgId: orgId,
                    lang: lang,
                    inter: inter
                }
            })
        }
    }

    const nullSafe = (str: string | undefined | null, postfix: string) => {
        return str ? str + postfix : ''
    }

    const getAddressTxt = (adr: IInfoGoAddress) => {
        let adrTxt = `${nullSafe(adr.buildingName, ', ')}${nullSafe(adr.buildingUnit, ', ')}${nullSafe(adr.street, ', ')}${nullSafe(adr.poBox, ', ')}${nullSafe(adr.ruralRoute, ', ')}${nullSafe(adr.city, ', ')}${nullSafe(adr.province, ' ')}${nullSafe(adr.postalCode, '')}`.trim()
        if (adrTxt.endsWith(',')) {
            return adrTxt.substring(0, adrTxt.length - 1)
        }
        return adrTxt
    }

    const getLeftMargin = () => {
        if (data?.infoGoPreview?.parentOrgs?.length > 0) {
            return (data.infoGoPreview.parentOrgs.length - 1) * 1.25 + 'rem'
        } else {
            return 0
        }
    }

    return (<>
        {loading && <SpinnerOverlay />}
        <Modal size="xl" isOpen={isOpen} toggle={toggle} scrollable backdrop="static">
            <ModalHeader toggle={toggle} tag={'h2'}>Preview - {inter ? 'Internet': 'Intranet'}</ModalHeader>
            <ModalBody className="infogo-preview">
                <div className="ontario-header">
                    <div className="ontario-row">
                        <div><img src="images/ontario-logo.svg" alt="Ontario Government"></img></div>

                        {inter &&
                            <div>
                                <a onClick={switchLang} className="ontario-header-button">
                                    {language === 'EN' ? 'Français' : 'English'}
                                </a>
                            </div>
                        }
                    </div>
                </div>
                <div className="ontario-subheader">
                    <div className="infogo-heading">INFO-GO</div>
                    <div className="ontario-row">{language === 'EN' ? 'Employee and organization directory' : 'Répertoire des employés et des bureaux'}</div>
                </div>

                {data?.infoGoPreview?.parentOrgs?.length > 0 &&
                    <nav aria-label="Organization hierarchy">
                        <ul>
                            {data?.infoGoPreview?.parentOrgs.map((e: IInfoGoOrgBase, idx: number) => (
                                <li className="treev" style={{marginLeft: (1.25 * idx) + 'rem'}}>
                                    {e.orgId !== '-1'
                                        ? <a tabIndex={0} onClick={event => selectOrg(event, e.orgId)} onKeyDown={event => selectOrg(event, e.orgId)}>{e.orgName}</a>
                                        : <span>{e.orgName}</span>
                                    } 
                                </li>
                            ))}
                        </ul>
                    </nav>
                }

                <section className="ontario-callout-card" style={{marginLeft: getLeftMargin()}}>
                    <h2 className="ontario-h5">{data?.infoGoPreview?.orgName}</h2>

                    {data?.infoGoPreview?.description && <>
                        <div>{data?.infoGoPreview?.description}</div>
                        <Spacer height="1.5rem"/>
                    </>}

                    <div>
                        {
                            phoneMap.size > 0 && Array.from(phoneMap.entries()).map(([key, value]) => (
                                <div className="row">
                                    <div className="col-md-4 ontario-label">
                                        {key}:
                                    </div>
                                    <div className="col-md-8">
                                        {value.map(v => (
                                            <div key={v.phoneId}>{v.phoneNumber} {v.phoneDescription ? <>- {v.phoneDescription}</> : ''}</div>
                                        ))}
                                    </div>
                                </div>
                            ))
                        }

                        {
                            data?.infoGoPreview?.emailList?.length > 0 &&
                            <div className="row">
                                <div className="col-md-4 ontario-label">
                                    {language === 'EN' ? 'Email:' : 'Courriel:'}
                                </div>
                                <div className="col-md-8">
                                    {data?.infoGoPreview?.emailList.map((e: IInfoGoEmail) => (
                                        <div key={e.emailId}>{e.email}</div>
                                    ))}
                                </div>
                            </div>
                        }

                        {
                            data?.infoGoPreview?.netAddresses?.length > 0 &&
                            <div className="row">
                                <div className="col-md-4 ontario-label">
                                    {language === 'EN' ? 'Website:' : 'Site Web:'} 
                                </div>
                                <div className="col-md-8">
                                    {data?.infoGoPreview?.netAddresses.map((e: IInfoGoNetAddress) => (
                                        <div key={e.netAddressId}>{e.netAddress}</div>
                                    ))}
                                </div>
                            </div>
                        }

                        {
                            primaryAddress &&
                            <div className="row">
                                <div className="col-md-4 ontario-label">
                                    {language === 'EN' ? 'Primary address:' : 'Adresse principale:'}
                                </div>
                                <div className="col-md-8">
                                    {getAddressTxt(primaryAddress)}
                                </div>
                            </div>
                        }

                        {
                            mailingAddress &&
                            <div className="row">
                                <div className="col-md-4 ontario-label">
                                    {language === 'EN' ? 'Mailing address:' : 'Adresse postale:'}
                                </div>
                                <div className="col-md-8">
                                    {getAddressTxt(mailingAddress)}
                                </div>
                            </div>
                        }

                        {
                            otherAddresses?.length > 0 &&
                            <div className="row">
                                <div className="col-md-4 ontario-label">
                                    {language === 'EN' ? 'Other addresses:' : 'Autres adresses:'}
                                </div>
                                <div className="col-md-8">
                                    {otherAddresses.map((e: IInfoGoAddress) => (
                                        <div className="row">
                                            <span key={e.addressUsageId}>{getAddressTxt(e)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        }
                    </div>
                </section>

                {data?.infoGoPreview?.empList?.length > 0 &&
                    <section style={{marginLeft: getLeftMargin()}}>
                        <h3 className="ontario-h5">{language === 'EN' ? 'Employees:': 'Employés:'}</h3>
                        <ul className="preview-list">
                        {
                            data.infoGoPreview.empList.map((e: IInfoGoEmployee) => (
                                <li key={e.positionId}>
                                    <ul className="ul-container-inline">
                                        <li>{e.name}</li>
                                        <li>{e.jobTitle}</li>
                                        {e.phoneNumber && <li>{e.phoneNumber}</li>}
                                        {e.email && <li>{e.email}</li>}
                                    </ul>
                                </li>
                            ))
                        }
                        </ul>
                    </section>
                }

                {data?.infoGoPreview?.childOrgs?.length > 0 &&
                    <section style={{marginLeft: getLeftMargin()}}>
                        <h3 className="ontario-h5">{language === 'EN' ? 'Reporting organizations:': 'Bureaux subsidiaires:'}</h3>
                        <ul className="preview-list">
                        {
                            data.infoGoPreview.childOrgs.map((e: IInfoGoOrgBase) => (
                                <li key={e.orgId}>
                                    <a onClick={(event)=>selectOrg(event, e.orgId)}>{e.orgName}</a>
                                </li>
                            ))
                        }
                        </ul>
                    </section>
                }

                {data?.infoGoPreview?.indirectOrgs?.length > 0 &&
                    <section style={{marginLeft: getLeftMargin()}}>
                        <h3 className="ontario-h5">{language === 'EN' ? 'Works with:': 'Collabore avec:'}</h3>
                        <ul className="preview-list">
                            {orgCategories.map((category: ICategory) => (
                                <li key={category.id}>
                                    <h4 className="ontario-h6">{category.categoryName}</h4>
                                    <ul>
                                        {associatedOrgsMap.get(category.id.toString())?.map((org: IInfoGoOrgBase) => (
                                            <li key={org.orgId}>{org.relName} <a onClick={(event) => selectOrg(event, org.orgId)}>{org.orgName}</a></li>
                                        ))}
                                    </ul>
                                </li>
                            ))}

                        </ul>
                    </section>
                }

                <div className="ontario-footer"></div>

            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={toggle}>
                    <CancelIcon/> Close
                </Button>
            </ModalFooter>
        </Modal></>
    )
})

export default Preview
